import React, { useEffect, useState } from 'react';
import { Download, Share, SquarePlus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  subscribe,
  getDeferredPrompt,
  clearDeferredPrompt,
  isStandalone,
  getPlatform,
} from '../pwaInstall';

const InstallPWAButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(getDeferredPrompt);
  const [isInstalled, setIsInstalled] = useState(isStandalone);
  const [showHelp, setShowHelp] = useState(false);
  const { t } = useTranslation();
  const { isIOS, isInAppBrowser } = getPlatform();

  useEffect(() => {
    const sync = () => {
      setDeferredPrompt(getDeferredPrompt());
      setIsInstalled(isStandalone());
    };
    sync();
    const unsubscribe = subscribe(sync);
    const standaloneQuery = window.matchMedia('(display-mode: standalone)');
    standaloneQuery.addEventListener?.('change', sync);
    return () => {
      unsubscribe();
      standaloneQuery.removeEventListener?.('change', sync);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      setShowHelp(true);
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    // A prompt can only be used once; Chrome fires a new one later if dismissed.
    clearDeferredPrompt();
    if (outcome === 'accepted') setIsInstalled(true);
  };

  if (isInstalled) return null;

  const renderSteps = () => {
    if (isInAppBrowser) {
      return <p>{t('installInAppBrowser')}</p>;
    }
    if (isIOS) {
      return (
        <ol className="space-y-3">
          <li className="flex items-start gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#FF9200] text-sm font-bold text-white">1</span>
            <span>
              {t('installIosStep1')} <Share size={18} className="inline align-text-bottom text-blue-500" aria-hidden="true" />
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#FF9200] text-sm font-bold text-white">2</span>
            <span>
              {t('installIosStep2')} <SquarePlus size={18} className="inline align-text-bottom" aria-hidden="true" />
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#FF9200] text-sm font-bold text-white">3</span>
            <span>{t('installIosStep3')}</span>
          </li>
        </ol>
      );
    }
    return <p>{t('installBrowserMenu')}</p>;
  };

  return (
    <>
      <button
        type="button"
        className="inline-flex items-center gap-2 rounded-full bg-[#FF9200] px-3 py-2 text-sm font-semibold text-white transition hover:bg-orange-500 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#120239]"
        onClick={handleInstallClick}
      >
        <Download size={16} aria-hidden="true" />
        {t('install')}
      </button>

      {showHelp && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center"
          onClick={() => setShowHelp(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="install-help-title"
            className="w-full max-w-sm rounded-2xl bg-white p-5 text-gray-900 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 id="install-help-title" className="text-lg font-bold">
                {t('installTitle')}
              </h2>
              <button
                type="button"
                onClick={() => setShowHelp(false)}
                className="rounded-full p-1 text-gray-500 hover:bg-gray-100"
                aria-label={t('close')}
              >
                <X size={20} />
              </button>
            </div>
            {renderSteps()}
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPWAButton;
